"use client";
import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";

import { useLogin } from "@/hooks/auth";
import {
  type CachedSeasonDecoration,
  readCachedSeasonDecoration,
  resolveSeasonTheme,
  type SeasonDecoration,
  type SeasonTheme,
  toJstDateString,
  writeCachedSeasonDecoration,
} from "@/libs/seasonDecoration";

import { activeSeasonDecorationQuery } from "./_graphql";

// 判定に使う日付はページを読み込んだ時点で固定する。
// 画面を開いたまま日付をまたいでも、その場では装飾を切り替えない
let pageLoadJstDate: string | null = null;
function getPageLoadJstDate(): string {
  pageLoadJstDate ??= toJstDateString(new Date());
  return pageLoadJstDate;
}

/**
 * 勤務確定モーダル・フッターに出すシーズン装飾のテーマ。装飾しないときは null
 * 取得に失敗しても画面の表示・操作は妨げない（前回の結果か通常デザインで出す）
 */
export function useSeasonTheme(): SeasonTheme | null {
  const { isLogin } = useLogin();
  // ワーカー向け API はログイン必須のため、未ログインでは問い合わせない（前回の結果で判定する）
  // フッターは画面ごとに作り直されるので、取得に成功した結果はページ内で使い回す（失敗したときだけ次の画面で問い合わせ直す）
  const { data } = useQuery(activeSeasonDecorationQuery, {
    skip: !isLogin,
    fetchPolicy: "cache-first",
  });
  // localStorage と端末時刻はブラウザでしか読めないため、最初の表示は表示後に読む（サーバー描画との食い違いを防ぐ）。
  // 画面遷移でフッターが作り直されたときは、ページ読み込み時の日付が決まっているので最初の描画から判定し、装飾の点滅を防ぐ
  const [cached, setCached] = useState<CachedSeasonDecoration>(() =>
    pageLoadJstDate ? readCachedSeasonDecoration() : undefined,
  );
  const [today, setToday] = useState<string | null>(() => pageLoadJstDate);

  useEffect(() => {
    setCached(readCachedSeasonDecoration());
    setToday(getPageLoadJstDate());
  }, []);

  const fetched: SeasonDecoration | null | undefined = data
    ? (data.activeSeasonDecoration ?? null)
    : undefined;

  useEffect(() => {
    if (fetched !== undefined) writeCachedSeasonDecoration(fetched);
  }, [fetched]);

  if (today === null) return null;
  return resolveSeasonTheme({ fetched, cached, today });
}
